import { useDebounceValue } from "usehooks-ts";
import { useEffect, useRef, useState } from "react";
import type { ComponentProps, FocusEvent } from "react";
import type { Input } from "./input";

type TitleInputProps = ComponentProps<typeof Input> & {
  onDebounce?: (value: string) => void;
  debounceMs?: number;
};

const TitleInput = ({
  onDebounce,
  debounceMs = 500,
  onChange,
  onFocus,
  onBlur,
  value,
  ...props
}: TitleInputProps) => {
  const [internalValue, setInternalValue] = useState<string>(value?.toString() ?? "");
  const [debouncedValue] = useDebounceValue(internalValue, debounceMs);
  const onDebounceRef = useRef(onDebounce);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    onDebounceRef.current = onDebounce;
  }, [onDebounce]);

  useEffect(() => {
    // Don't overwrite what the user is actively typing with a stale server
    // value arriving mid-keystroke (e.g. a previous debounce mutation resolving
    // while the user has already typed ahead).
    if (!isFocusedRef.current) {
      setInternalValue(value?.toString() ?? "");
    }
  }, [value]);

  useEffect(() => {
    if (!onDebounceRef.current) return;
    onDebounceRef.current(debouncedValue);
  }, [debouncedValue]);

  return (
    <input
      value={internalValue}
      onChange={(event) => {
        setInternalValue(event.target.value);
        onChange?.(event);
      }}
      onFocus={(event: FocusEvent<HTMLInputElement>) => {
        isFocusedRef.current = true;
        onFocus?.(event);
      }}
      onBlur={(event: FocusEvent<HTMLInputElement>) => {
        isFocusedRef.current = false;
        onBlur?.(event);
      }}
      {...props}
    />
  );
};

export { TitleInput };
